import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { useStore } from '../../store';
import { COUNTRIES } from '../../types';
import { t } from '../../lib/i18n';
import toast from 'react-hot-toast';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: 'login' | 'register';
}

export const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, defaultTab = 'login' }) => {
  const [tab, setTab] = useState<'login' | 'register'>(defaultTab);
  const [loading, setLoading] = useState(false);
  const { login, register, lang } = useStore();

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');

  // Register state
  const [reg, setReg] = useState({
    firstName: '', lastName: '', email: '', password: '', country: 'France', city: '',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const err = login(loginEmail, loginPw);
    setLoading(false);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(`${t(lang, 'toast_welcome')}!`);
    onClose();
    setLoginEmail(''); setLoginPw('');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const err = register(reg);
    setLoading(false);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(`${t(lang, 'toast_joined')}!`);
    onClose();
    setReg({ firstName: '', lastName: '', email: '', password: '', country: 'France', city: '' });
  };

  return (
    <Modal open={open} onClose={onClose} title={tab === 'login' ? t(lang, 'auth_signin') : t(lang, 'auth_register')} maxWidth="480px">
      <div className="auth-tabs">
        <button className={`tab ${tab === 'login' ? 'active' : ''}`} onClick={() => setTab('login')}>
          {t(lang, 'auth_signin')}
        </button>
        <button className={`tab ${tab === 'register' ? 'active' : ''}`} onClick={() => setTab('register')}>
          {t(lang, 'auth_register')}
        </button>
      </div>

      {tab === 'login' ? (
        <form onSubmit={handleLogin} className="auth-form" noValidate>
          <div className="form-group">
            <label className="form-label">{t(lang, 'auth_email')}</label>
            <input className="form-input" type="email" value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)} placeholder="outlaw@frontier.com"
              autoComplete="email" required maxLength={254} />
          </div>
          <div className="form-group">
            <label className="form-label">{t(lang, 'auth_password')}</label>
            <input className="form-input" type="password" value={loginPw}
              onChange={e => setLoginPw(e.target.value)} placeholder="••••••••"
              autoComplete="current-password" required />
          </div>
          <button className="btn btn-gold btn-full" type="submit" disabled={loading}>
            {loading ? '...' : `🤠 ${t(lang, 'auth_login_btn')}`}
          </button>
          <p className="auth-demo-hint">Demo: elena@example.com / pass1234</p>
        </form>
      ) : (
        <form onSubmit={handleRegister} className="auth-form" noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t(lang, 'auth_firstname')}</label>
              <input className="form-input" value={reg.firstName}
                onChange={e => setReg(r => ({ ...r, firstName: e.target.value }))}
                placeholder="Jesse" maxLength={50} required />
            </div>
            <div className="form-group">
              <label className="form-label">{t(lang, 'auth_lastname')}</label>
              <input className="form-input" value={reg.lastName}
                onChange={e => setReg(r => ({ ...r, lastName: e.target.value }))}
                placeholder="James" maxLength={50} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t(lang, 'auth_email')}</label>
            <input className="form-input" type="email" value={reg.email}
              onChange={e => setReg(r => ({ ...r, email: e.target.value }))}
              placeholder="outlaw@frontier.com" autoComplete="email" required maxLength={254} />
          </div>
          <div className="form-group">
            <label className="form-label">{t(lang, 'auth_password')}</label>
            <input className="form-input" type="password" value={reg.password}
              onChange={e => setReg(r => ({ ...r, password: e.target.value }))}
              placeholder="••••••••" autoComplete="new-password" required minLength={8} maxLength={128} />
            <span className="form-hint">{t(lang, 'auth_password_hint')}</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t(lang, 'auth_country')}</label>
              <select className="form-input" value={reg.country}
                onChange={e => setReg(r => ({ ...r, country: e.target.value }))}>
                {COUNTRIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t(lang, 'auth_city')}</label>
              <input className="form-input" value={reg.city}
                onChange={e => setReg(r => ({ ...r, city: e.target.value }))}
                placeholder="Paris" maxLength={80} required />
            </div>
          </div>
          <button className="btn btn-gold btn-full" type="submit" disabled={loading}>
            {loading ? '...' : `🏹 ${t(lang, 'auth_register_btn')}`}
          </button>
        </form>
      )}
    </Modal>
  );
};
